import PropTypes from 'prop-types';
import React, { useRef, useCallback } from 'react';
import { navigate } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import {
  CountryLanguageSelector,
  CountryLanguageSelectorDrawer,
} from 'lidya-common-components';
import {
  availableLanguages,
  COMPONENTS,
  COUNTRY_LANGUAGE_LIST,
} from './webSiteLanguages';
import { useGlobalData } from '../../providers/globalDataProvider/globalDataProvider.useGlobalData';
import { setLocalLanguage } from '../../providers/globalDataProvider/globalDataProvider.provider';

import { buildLink } from './contextLink';

import './languagePicker.less';

const LanguagePicker = ({
  className,
  showLabels,
  inlineLanguage,
  countryLanguageList,
  compactMode,
}) => {
  const {
    country, language, path, activeComponent,
  } = useGlobalData();
  const { t } = useTranslation();
  const nCountryRef = useRef();
  const nLanguageRef = useRef();

  const getTranslatedList = useCallback(() => {
    const translatedList = JSON.parse(JSON.stringify(countryLanguageList));
    Object.keys(translatedList).forEach((key) => {
      const languages = translatedList[key].languages.map((lang) => ({
        ...lang,
        name: t(lang.name),
      }));
      translatedList[key] = {
        ...translatedList[key],
        name: t(translatedList[key].name),
        languages,
      };
    });
    return translatedList;
  }, [countryLanguageList, t]);

  const onLanguageChange = useCallback(
    ({ nCountry, nLanguage }) => {
      if (nCountry) nCountryRef.current = nCountry;
      if (nLanguage) nLanguageRef.current = nLanguage;
      const { current: countryRef } = nCountryRef;
      const { current: languageRef } = nLanguageRef;

      if (countryRef && languageRef) {
        const countryConfigs = availableLanguages.find(
          (c) => c.country === countryRef,
        ) || { links: [] };
        const linkConfigs = countryConfigs.links.find(
          (config) => config.component === activeComponent,
        )
          || countryConfigs.links.find(
            (config) => config.component === COMPONENTS.HOME,
          );
        const slug = linkConfigs[`slug_${languageRef}`];
        const link = buildLink({
          pathname: slug,
          country: countryRef,
          language: languageRef,
        });
        nCountryRef.current = undefined;
        nLanguageRef.current = undefined;
        if (link !== path && !compactMode) navigate(link);
      }
    },
    [activeComponent, compactMode, path],
  );

  const params = {
    countryCode: country,
    onLanguageChange: (val) => onLanguageChange({ nLanguage: val }),
    onCountryChange: (val) => onLanguageChange({ nCountry: val }),
    countryList: getTranslatedList(),
    className: `${className} -${country}`,
    showLabels,
    inlineLanguage,
    languageCode: language,
  };

  const mobileHtml = !compactMode ? (
    <CountryLanguageSelectorDrawer
      {...params}
      key={`${language}_compactMode`}
    />
  ) : (
    <select
      name="CountryLanguageSelector"
      id="countryLanguageSelector"
      className="a-input a-field__input a-input__select a-fieldGroup__selectField -not-readonly -pristine -not-readonly"
      onChange={(event) => {
        event.preventDefault();
        const eCountry = event.target.value;
        const countryConfigs = availableLanguages.find(
          (c) => c.country === eCountry,
        );
        const eLanguage = countryConfigs.languages[0];
        setLocalLanguage({ country: eCountry, language: eLanguage });
      }}
      key={`${language}_selectMode`}
    >
      {Object.keys(params.countryList).map((code) => (
        <option
          value={params.countryList[code].code}
          key={params.countryList[code].code}
        >
          {params.countryList[code].name}
        </option>
      ))}
    </select>
  );

  const desktopHtml = !compactMode ? (
    <CountryLanguageSelector {...params} key={`${language}_regular`} />
  ) : null;
  return [desktopHtml, mobileHtml];
};

LanguagePicker.propTypes = {
  className: PropTypes.string,
  compactMode: PropTypes.bool,
  countryLanguageList: PropTypes.shape({}),
  inlineLanguage: PropTypes.bool,
  showLabels: PropTypes.bool,
};

LanguagePicker.defaultProps = {
  className: undefined,
  compactMode: false,
  countryLanguageList: COUNTRY_LANGUAGE_LIST,
  inlineLanguage: undefined,
  showLabels: undefined,
};

export default LanguagePicker;
